// The dashboard as last read, kept in this browser so that opening or
// reloading it shows the rows at once (see DashboardStore.open), and the
// attention inputs behind it, kept where the browser extension keeps its own.
//
// Both are runtime caches (runtime/cache.ts) in the signed-in account's
// IndexedDB, whose key names the server's origin, the account, the layout
// of their records (SNAPSHOT_VERSION) and an inputs hash of what their
// values are worked out from (DASH_DATA_VERSION: the queries, the rules
// that turn their answers into the engine's input, and the engine build).
// A record is read back only under the same key, so a deploy never shows
// data of the old shape; two builds open at once (during a deploy) keep
// their own records, and `gc` deletes another build's once nobody holds
// them and they are too old to be of use.
//
// One record per part: the saved copy's meta (whose and when), each
// section, each pull request's attention set, and the native stack members
// no section loaded; one per pull request's attention inputs. Records that
// depend on a pull request (its attention, its inputs) name it as their
// subject, so the account's invalidation log drops them wherever they are
// read once the pull request changed after they were worked out: the app
// wrote to it, the viewer opened it, or a notice named it, in this tab or
// another, before or after this page opened.
import { identityUser, onIdentityChange } from '../api/identity';
import * as queries from '../api/queries';
import type { DashPr } from '../api/queries';
import type { AttentionMember } from '../engine/types';
import type { Section } from '../logic/sections';
import { ATTENTION_RULES_VERSION } from '../logic/timeline';
import {
  cacheKey,
  hashInputs,
  InvalidationLog,
  LOG_KEPT_MS,
  memoryBackend,
  openCache,
  type Backend,
  type Cache,
  type InputsHash,
  type Entry,
  type ReadTicket,
  type Write,
  type WriteResult,
} from '../runtime/cache';
import { sameData } from '../logic/sameData';
import { idbBackend } from '../runtime/idb';
import { INPUTS_TTL_MS, MAX_ENTRIES, prKeyOf, sharedAttentionInputs, type InputsStore, type KeptInputs } from './attentionInputs';
import type { SectionCut } from './dashboard/search';

/** The layout of the saved records; part of every cache key. 4: one record per part, on the runtime cache. */
export const SNAPSHOT_VERSION = 4;

/**
 * Age past which a saved copy shown on opening the dashboard is checked
 * against GitHub in the background. A younger one is shown as it is, so it
 * may show, for up to this long, a turn that ended on GitHub itself
 * (accepted: the top bar says how old the rows are). What the viewer did
 * in Changeling is never shown stale (see the invalidation log).
 */
export const SNAPSHOT_STALE_MS = 2 * 60_000;

/**
 * How long forgets are known (the invalidation log's horizon). A saved
 * section older than this shows no saved attention (a forget since may have
 * been pruned).
 */
export const FORGETS_KEPT_MS = LOG_KEPT_MS;

/** The inputs hash for records produced from these parts (query texts, rule versions) and this engine build. */
export function dataVersion(parts: string[], engine: string): InputsHash {
  return hashInputs(...parts, `engine ${engine}`);
}

/** The engine's build (a hash of its WebAssembly; see web/src/dev/engineBuild.ts). */
const ENGINE_BUILD: string = import.meta.env.VITE_ENGINE_BUILD ?? 'unknown';

/**
 * What the saved data depends on besides the engine: every query text and
 * value api/queries.ts exports (by name, so a query added there is covered
 * without a list to keep), and ATTENTION_RULES_VERSION.
 */
export function dashVersionParts(): string[] {
  const parts = Object.entries(queries)
    .filter(([, v]) => typeof v === 'string' || typeof v === 'number')
    .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0))
    .map(([, v]) => String(v));
  return [...parts, `rules ${ATTENTION_RULES_VERSION}`];
}

/** The inputs hash of the rows, attention inputs and attention this build reads and works out. */
export const DASH_DATA_VERSION: InputsHash = dataVersion(dashVersionParts(), ENGINE_BUILD);

/** One section as last read. */
export interface SavedSection {
  /** The section's definition (`sectionKey`): a copy shows only for the same one. */
  key: string;
  /** When its rows were read from GitHub (epoch ms). */
  at: number;
  /** Its pull requests, every one its searches loaded (an attention section's left-out ones too). */
  prs: DashPr[];
  cut: SectionCut | null;
  /**
   * How many matches did not load. Their failure is not saved: it held
   * then, and a section saved with some is read again on opening.
   */
  unloaded: number;
}

export interface DashSnapshot {
  /** When it was written (epoch ms). */
  savedAt: number;
  /** The viewer it was read for. */
  login: string;
  sections: SavedSection[];
  /**
   * Each pull request's attention set where it was known, by node ID; on
   * reading, only those whose pull request has not changed since.
   */
  attention: Record<string, AttentionMember[]>;
  /**
   * The ticket each of those was worked out under (that of the work that
   * read what it was worked out from), by node ID: its record is stored
   * under it. In memory only: a ticket is not data.
   */
  attentionTickets: Record<string, ReadTicket>;
  /**
   * The native stack members no section loaded, as read by ID, and those
   * GitHub did not show the viewer (`memberKey`); see `DashboardStore.pool`.
   */
  outside: { prs: DashPr[]; hidden: string[] };
}

/** Where the saved copy lives. Reads that fail are misses; writes that fail say so. */
export interface SnapshotStore {
  /** A ticket for work beginning now: what it saves is not kept for a pull request that changes after. */
  begin(): ReadTicket;
  /** The saved copy, or null when there is none or it cannot be read. */
  read(): Promise<DashSnapshot | null>;
  /**
   * Replace the saved copy with `s`: its sections under `ticket` (the
   * work that read them), each pull request's attention under the ticket
   * it was worked out under (`attentionTickets`), so one changed since is
   * left out however late it is saved. Only records that changed are
   * written, in one write, all or nothing. A copy that could not be
   * replaced is deleted: it must not outlive the newer read.
   */
  write(s: DashSnapshot, ticket: ReadTicket): Promise<WriteResult>;
  /** Delete what is saved for pull request `subject` (`prKeyOf`): its change could not be logged. */
  drop(subject: string): Promise<WriteResult>;
}

/** A section's identity for its saved copy: what it searches and keeps. */
export const sectionKey = (s: Section) => JSON.stringify([s.title, s.queries, !!s.attention]);

const isObject = (x: unknown): x is Record<string, unknown> => typeof x === 'object' && x !== null && !Array.isArray(x);
const isCount = (x: unknown): x is number => typeof x === 'number' && Number.isFinite(x) && x >= 0;
const isList = (x: unknown) => isObject(x) && Array.isArray(x.nodes);
const isLogin = (x: unknown) => x === null || (isObject(x) && typeof x.login === 'string');

/** Whether `x` has every field of a row the dashboard reads, of the right kind. */
export function isPr(x: unknown): x is DashPr {
  if (!isObject(x)) return false;
  const repo = x.repository;
  return (
    typeof x.id === 'string' &&
    isCount(x.number) &&
    typeof x.title === 'string' &&
    typeof x.url === 'string' &&
    typeof x.isDraft === 'boolean' &&
    typeof x.state === 'string' &&
    typeof x.createdAt === 'string' &&
    typeof x.updatedAt === 'string' &&
    isCount(x.additions) &&
    isCount(x.deletions) &&
    (x.reviewDecision === null || typeof x.reviewDecision === 'string') &&
    isLogin(x.author) &&
    isObject(repo) &&
    typeof repo.nameWithOwner === 'string' &&
    typeof repo.name === 'string' &&
    isLogin(repo.owner) &&
    repo.owner !== null &&
    typeof x.baseRefName === 'string' &&
    typeof x.headRefName === 'string' &&
    (x.headRepository === null || (isObject(x.headRepository) && typeof x.headRepository.nameWithOwner === 'string')) &&
    (x.stack === null || (isObject(x.stack) && isList(x.stack.entries))) &&
    (x.stackEntry === null || isObject(x.stackEntry)) &&
    isList(x.reviewRequests) &&
    isList(x.commits)
  );
}

function isSection(x: unknown): x is SavedSection {
  if (!isObject(x) || typeof x.key !== 'string' || !isCount(x.at) || !Array.isArray(x.prs) || !x.prs.every(isPr)) return false;
  const cut = x.cut;
  return (cut === null || (isObject(cut) && isCount(cut.checked) && isCount(cut.total))) && isCount(x.unloaded);
}

const isMembers = (x: unknown): x is AttentionMember[] =>
  Array.isArray(x) && x.every((m) => isObject(m) && typeof m.login === 'string' && isObject(m.reason) && typeof m.reason.kind === 'string');

/** One stored part of the saved copy. */
export type SnapshotRecord =
  /** Whose copy, when saved, and its sections' keys in order. */
  | { kind: 'meta'; login: string; savedAt: number; order: string[] }
  | { kind: 'section'; section: SavedSection }
  /** A pull request's attention set; `pr` is its `prKeyOf` key, the record's subject. */
  | { kind: 'attention'; pr: string; members: AttentionMember[] }
  | { kind: 'outside'; prs: DashPr[]; hidden: string[] };

/** The record in `raw`, or null when it is not one. */
export function decodeSnapshotRecord(raw: unknown): SnapshotRecord | null {
  if (!isObject(raw)) return null;
  switch (raw.kind) {
    case 'meta':
      return typeof raw.login === 'string' && isCount(raw.savedAt) && Array.isArray(raw.order) && raw.order.every((k) => typeof k === 'string')
        ? (raw as SnapshotRecord)
        : null;
    case 'section':
      return isSection(raw.section) ? (raw as SnapshotRecord) : null;
    case 'attention':
      return typeof raw.pr === 'string' && isMembers(raw.members) ? (raw as SnapshotRecord) : null;
    case 'outside':
      return Array.isArray(raw.prs) && raw.prs.every(isPr) && Array.isArray(raw.hidden) && raw.hidden.every((k) => typeof k === 'string')
        ? (raw as SnapshotRecord)
        : null;
    default:
      return null;
  }
}

const isKept = (x: unknown): x is KeptInputs =>
  isObject(x) &&
  typeof x.key === 'string' &&
  typeof x.updatedAt === 'string' &&
  typeof x.resolved === 'string' &&
  isCount(x.at) &&
  isObject(x.inputs) &&
  isList(x.inputs.timelineItems) &&
  isList(x.inputs.reviewThreads);

/**
 * Whether what is stored is already what a save wants there: the same
 * record, and, for a pull request's attention, worked out by the same read
 * (an answer worked out again is stored as of its newer read).
 */
function sameRecord(stored: Entry<SnapshotRecord> | undefined, wanted: { value: SnapshotRecord; ticket: ReadTicket }): boolean {
  if (!stored || stored.value.kind !== wanted.value.kind) return false;
  if (wanted.value.kind === 'attention' && stored.basis !== wanted.ticket.basis) return false;
  return sameData(stored.value, wanted.value);
}

/** Where and for whom a cache keeps its records. */
export interface StorageOptions {
  origin: string;
  /** The account (user ID) the records are for. */
  account: string;
  /** The signed-in account's IndexedDB by default. */
  backend?: Backend;
  /** The account's invalidation log: records of a pull request changed since are dropped. */
  log?: InvalidationLog | null;
  /** The inputs hash (DASH_DATA_VERSION by default). */
  version?: InputsHash;
}

/** The keys earlier builds kept these in, before the runtime cache; deleted once read past. */
const legacyPrefixes = (origin: string) => [`dashboard-snapshot:${origin}:`, `dashboard-forgets:${origin}`, `dashboard-attention-inputs:${origin}`];

/** Delete what earlier builds kept under `prefixes` (in the background). */
async function dropLegacy(backend: Backend, prefixes: string[]) {
  try {
    const old = (await Promise.all(prefixes.map((p) => backend.scan(p)))).flat();
    if (old.length) await backend.write(old.map(([k]) => [k, undefined] as const));
  } catch {
    /* tried again on the next open */
  }
}

/** A cache of these options, held by a lease from its first read and collected then. */
function held<V>(c: Cache<V>) {
  let release: (() => void) | null = null;
  /** The collection the latest lease began (see `Closable.settled`). */
  let collected: Promise<unknown> = Promise.resolve();
  return {
    get collected() {
      return collected;
    },
    open() {
      if (release) return;
      release = c.hold();
      collected = c.gc().catch(() => {});
    },
    close() {
      release?.();
      release = null;
    },
  };
}

const keyOf = (o: StorageOptions) =>
  cacheKey({ origin: o.origin, account: o.account, schemaVersion: SNAPSHOT_VERSION, inputsHash: o.version ?? DASH_DATA_VERSION });

/** A store that can be let go of (another account signed in). */
export interface Closable {
  close(): void;
  /**
   * Resolves once the housekeeping its first read began in the background
   * is done: collecting what other builds kept, and deleting the records
   * earlier builds kept outside the runtime cache. At once before a read.
   */
  settled(): Promise<void>;
}

/** The saved dashboard for `opts.account` at `opts.origin` (see the file comment). */
export function cachedSnapshot(opts: StorageOptions): SnapshotStore & Closable {
  const backend = opts.backend ?? idbBackend();
  const log = opts.log ?? null;
  const base = { name: 'dashboard', key: keyOf(opts), decode: decodeSnapshotRecord, backend };
  const cache = log
    ? openCache<SnapshotRecord>({ ...base, log, subjects: (_k, v) => (v.kind === 'attention' ? [v.pr] : []) })
    : openCache<SnapshotRecord>(base);
  const lease = held(cache);
  let legacy: Promise<void> | null = null;
  const all = async () => {
    lease.open();
    legacy ??= dropLegacy(backend, legacyPrefixes(opts.origin).slice(0, 2));
    return cache.entries();
  };
  return {
    begin: () => cache.begin(),
    async read() {
      const entries = await all();
      let meta: { login: string; savedAt: number; order: string[] } | null = null;
      const sections: SavedSection[] = [];
      const attention: Record<string, AttentionMember[]> = {};
      const attentionTickets: Record<string, ReadTicket> = {};
      let outside: DashSnapshot['outside'] = { prs: [], hidden: [] };
      for (const [k, e] of entries) {
        const r = e.value;
        if (r.kind === 'meta') meta = r;
        else if (r.kind === 'section') sections.push(r.section);
        else if (r.kind === 'attention') {
          const id = k.slice('attention|'.length);
          attention[id] = r.members;
          attentionTickets[id] = e.ticket;
        } else outside = { prs: r.prs, hidden: r.hidden };
      }
      if (!meta) return null;
      const order = meta.order;
      sections.sort((a, b) => order.indexOf(a.key) - order.indexOf(b.key));
      return { savedAt: meta.savedAt, login: meta.login, sections, attention, attentionTickets, outside };
    },
    async write(s, ticket) {
      const stored = await all();
      const prOf = new Map(s.sections.flatMap((x) => x.prs.map((p) => [p.id, prKeyOf(p.repository.nameWithOwner, p.number)] as const)));
      // The records the copy is made of, each under the ticket of the read behind it.
      const wanted = new Map<string, { value: SnapshotRecord; ticket: ReadTicket }>();
      const put = (key: string, value: SnapshotRecord, under = ticket) => wanted.set(key, { value, ticket: under });
      put('meta', { kind: 'meta', login: s.login, savedAt: s.savedAt, order: s.sections.map((x) => x.key) });
      for (const section of s.sections) put(`section|${section.key}`, { kind: 'section', section });
      for (const [id, members] of Object.entries(s.attention)) {
        const pr = prOf.get(id);
        const under = s.attentionTickets[id];
        if (pr && under) put(`attention|${id}`, { kind: 'attention', pr, members }, under);
      }
      put('outside', { kind: 'outside', prs: s.outside.prs, hidden: s.outside.hidden });
      // Only what changed is written; what is no longer part of the copy is deleted.
      const writes: Write<SnapshotRecord>[] = [];
      for (const k of stored.keys()) if (!wanted.has(k)) writes.push({ key: k, value: null, ticket });
      for (const [k, w] of wanted) if (!sameRecord(stored.get(k), w)) writes.push({ key: k, ...w });
      const done = await cache.writeAll(writes);
      if (done.ok) return done;
      console.warn("Changeling couldn't save the dashboard in this browser; it loads from GitHub next time.");
      // A copy that could not be replaced must not outlive the newer read: drop it.
      await cache.writeAll([...new Set([...stored.keys(), ...wanted.keys()])].map((key) => ({ key, value: null, ticket })));
      return done;
    },
    async drop(subject) {
      const doomed = [...(await all())].filter(([, e]) => e.value.kind === 'attention' && e.value.pr === subject).map(([k]) => [k, null]);
      return cache.write(Object.fromEntries(doomed), cache.begin());
    },
    close: lease.close,
    settled: async () => void (await Promise.all([lease.collected, legacy])),
  };
}

/**
 * The attention inputs kept for `opts.account` at `opts.origin`, one
 * record per pull request (a forget rewrites one, not the map), for
 * INPUTS_TTL_MS, MAX_ENTRIES at most. The app and the browser extension
 * keep them alike.
 */
export function cachedInputs(opts: StorageOptions): InputsStore & Closable {
  const backend = opts.backend ?? idbBackend();
  const log = opts.log ?? null;
  const base = { name: 'attention-inputs', key: keyOf(opts), decode: (raw: unknown) => (isKept(raw) ? raw : null), backend, maxAgeMs: INPUTS_TTL_MS, limit: MAX_ENTRIES };
  const cache = log ? openCache<KeptInputs>({ ...base, log, subjects: (_k, v) => [v.key] }) : openCache<KeptInputs>(base);
  const lease = held(cache);
  let legacy: Promise<void> | null = null;
  return {
    async read() {
      lease.open();
      legacy ??= dropLegacy(backend, legacyPrefixes(opts.origin).slice(2));
      return Object.fromEntries([...(await cache.entries())].map(([id, e]) => [id, e.value]));
    },
    async write(changes) {
      // Each entry is stored under when its read began, so the log drops one read before a change however late it is saved.
      const now = cache.begin();
      const writes = Object.entries(changes).map(([key, e]) => ({ key, value: e, ticket: e ? cache.ticketAt(e.basis ?? e.at) : now }));
      if (!(await cache.writeAll(writes)).ok) {
        console.warn("Changeling couldn't save the dashboard's attention inputs in this browser; it reads them from GitHub again.");
        throw new Error('attention inputs not saved');
      }
    },
    close: lease.close,
    settled: async () => void (await Promise.all([lease.collected, legacy])),
  };
}

/** The app's storage for the account signed in now, once its log is open. */
interface AccountStorage {
  account: string;
  ready: Promise<{ snapshot: SnapshotStore & Closable; inputs: InputsStore & Closable; log: InvalidationLog | null }>;
}

let current: AccountStorage | null = null;
let wiredOrigin: string | null = null;
/** The account each ticket was issued for: a save begun for one account never lands in another's. */
const issuers = new WeakMap<ReadTicket, AccountStorage>();
/** Tickets while nobody is signed in (what they would save goes nowhere). */
const nobody = openCache<never>({
  name: 'nobody',
  key: cacheKey({ origin: 'nowhere', account: 'nobody', schemaVersion: 0, inputsHash: hashInputs() }),
  decode: () => null,
  backend: memoryBackend(),
});

function storageFor(origin: string): AccountStorage | null {
  const account = identityUser();
  if (!account) return null;
  if (current?.account === account) return current;
  const backend = idbBackend();
  const s: AccountStorage = {
    account,
    // The saved copy is read only once the log is open: what changed before this page opened drops its records.
    ready: InvalidationLog.open({ origin, account, backend })
      .catch(() => null)
      .then((log) => {
        const stores = { snapshot: cachedSnapshot({ origin, account, backend, log }), inputs: cachedInputs({ origin, account, backend, log }), log };
        if (current === s) {
          sharedAttentionInputs.persistIn(stores.inputs);
          sharedAttentionInputs.useLog(log);
        }
        return stores;
      }),
  };
  current = s;
  return s;
}

/**
 * The app's saved dashboard for the server at `origin`, for whichever
 * account is signed in: the page's shared attention inputs are kept beside
 * it, and forgets go to the account's invalidation log. Wired once per
 * page; a forget the log could not keep drops what the saved copy holds
 * for that pull request.
 */
export function dashboardStorage(origin: string): SnapshotStore {
  if (wiredOrigin === null) {
    wiredOrigin = origin;
    onIdentityChange(() => {
      const was = current;
      current = null;
      void was?.ready.then(({ snapshot, inputs, log }) => {
        snapshot.close();
        inputs.close();
        log?.close();
      });
    });
    sharedAttentionInputs.onForgetLost((key) => {
      void current?.ready.then(async ({ snapshot }) => {
        if (!(await snapshot.drop(key)).ok)
          console.warn("Changeling couldn't drop a changed pull request from the saved dashboard; it may show its old attention once.");
      });
    });
  }
  storageFor(origin);
  return {
    begin() {
      const s = storageFor(origin);
      const t = nobody.begin();
      if (s) issuers.set(t, s);
      return t;
    },
    read: async () => {
      const s = storageFor(origin);
      return s ? (await s.ready).snapshot.read() : null;
    },
    write: async (snap, ticket) => {
      const s = issuers.get(ticket);
      if (!s || s !== current) return { ok: false, failure: { kind: 'unavailable', message: 'another account signed in' } };
      return (await s.ready).snapshot.write(snap, ticket);
    },
    drop: async (subject) => {
      const s = storageFor(origin);
      return s ? (await s.ready).snapshot.drop(subject) : { ok: true, durable: false };
    },
  };
}

/**
 * The app's saved dashboard as plain rows, for a reader outside the
 * dashboard (a pull request's page looking for its stack): every
 * section's rows and the stack members read by ID, those GitHub does not
 * show the viewer, and when the oldest section was read. Null when
 * nothing is saved, or it cannot be read. It reads through
 * `dashboardStorage`, so the first call on a page wires that storage (its
 * identity and lost-forget handlers, the shared attention inputs' store)
 * as the dashboard's own first use does.
 */
export async function savedDashboardRows(): Promise<{ prs: DashPr[]; hidden: string[]; at: number } | null> {
  if (typeof location === 'undefined') return null;
  const snap = await dashboardStorage(location.origin).read();
  if (!snap?.sections.length) return null;
  return {
    prs: [...snap.sections.flatMap((s) => s.prs), ...snap.outside.prs],
    hidden: snap.outside.hidden,
    at: Math.min(...snap.sections.map((s) => s.at)),
  };
}
